import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { uploadPhoto, getAlbumMetadata } from '../services/albumService';
import { useAuth } from '../contexts/AuthContext';
import { getSiteConfig } from '../services/firestoreService';
import { isUploadPinCookieValid, setUploadPinCookie, clearUploadPinCookie } from '../utils/cookieUtils';
import PinModal from '../components/PinModal';

const PhotoUploadPage = () => {
  const { albumId, uploadKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const albumTitle = location.state?.albumTitle || albumId;
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [validatingKey, setValidatingKey] = useState(!!uploadKey);
  const [validKey, setValidKey] = useState(false);
  const [albumMetadata, setAlbumMetadata] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [uploadPin, setUploadPin] = useState('202526');
  const [uploadPinUpdatedAt, setUploadPinUpdatedAt] = useState(null);
  const [pinValidated, setPinValidated] = useState(false);
  const [validatingPin, setValidatingPin] = useState(false);

  // Load site config for upload PIN
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await getSiteConfig();
        setUploadPin(config.uploadPin || '202526');
        setUploadPinUpdatedAt(config.uploadPinUpdatedAt);
      } catch (error) {
        console.error('Error loading site config:', error);
      }
    };
    loadConfig();
  }, []);

  // Validate upload key if present
  useEffect(() => {
    const validateUploadKey = async () => {
      if (uploadKey) {
        try {
          const metadata = await getAlbumMetadata(albumId);
          if (metadata.uploadKey === uploadKey) {
            setValidKey(true);
            setAlbumMetadata(metadata);
          } else {
            alert('Invalid upload link. Please check the URL.');
            navigate('/');
          }
        } catch (error) {
          console.error('Error validating upload key:', error);
          alert('Invalid upload link. Please check the URL.');
          navigate('/');
        } finally {
          setValidatingKey(false);
        }
      }
    };

    validateUploadKey();
  }, [albumId, uploadKey, navigate]);

  // Validate PIN for unauthenticated users (only for non-uploadKey routes)
  useEffect(() => {
    const validatePin = async () => {
      // Skip PIN validation if authenticated or using uploadKey
      if (isAuthenticated || uploadKey) {
        setPinValidated(true);
        return;
      }

      setValidatingPin(true);
      const isValid = await isUploadPinCookieValid(uploadPin, uploadPinUpdatedAt);
      if (isValid) {
        setPinValidated(true);
      } else {
        clearUploadPinCookie();
        setShowPinModal(true);
      }
      setValidatingPin(false);
    };

    // Only validate PIN after uploadPin is loaded
    if (uploadPin) {
      validatePin();
    }
  }, [isAuthenticated, uploadKey, uploadPin, uploadPinUpdatedAt]);

  const handlePinValidated = async (pin) => {
    await setUploadPinCookie(pin, uploadPinUpdatedAt);
    setPinValidated(true);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const mediaFiles = files.filter(file =>
      file.type.startsWith('image/') || file.type.startsWith('video/')
    );

    // Don't create preview URLs to avoid memory issues with large batches
    const filesWithoutPreviews = mediaFiles.map(file => {
      const isVideo = file.type.startsWith('video/');
      return {
        file,
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        size: file.size,
        isVideo: isVideo
      };
    });

    setSelectedFiles(prev => [...prev, ...filesWithoutPreviews]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const mediaFiles = files.filter(file =>
      file.type.startsWith('image/') || file.type.startsWith('video/')
    );

    // Don't create preview URLs to avoid memory issues with large batches
    const filesWithoutPreviews = mediaFiles.map(file => {
      const isVideo = file.type.startsWith('video/');
      return {
        file,
        id: Math.random().toString(36).substr(2, 9),
        name: file.name,
        size: file.size,
        isVideo: isVideo
      };
    });

    setSelectedFiles(prev => [...prev, ...filesWithoutPreviews]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeFile = (id) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    const newProgress = {};
    
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const fileData = selectedFiles[i];
        newProgress[fileData.id] = 0;
        setUploadProgress({ ...newProgress });
        
        await uploadPhoto(albumId, fileData.file);
        
        newProgress[fileData.id] = 100;
        setUploadProgress({ ...newProgress });
      }
      
      // Clear files and progress
      setSelectedFiles([]);
      setUploadProgress({});
      
      // Navigate based on context
      if (uploadKey) {
        // For anonymous uploads, navigate to album only if it's not restricted
        if (albumMetadata?.isRestricted) {
          alert('Photos uploaded successfully! Thank you for contributing.');
        } else {
          alert('Photos uploaded successfully! Redirecting to album...');
          setTimeout(() => navigate(`/album/${albumId}`), 1000);
        }
      } else {
        // For authenticated uploads, always navigate back to album
        navigate(`/album/${albumId}`);
      }
      
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Error uploading some photos. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Show loading state while validating upload key
  if (validatingKey) {
    return (
      <div className="w-full p-8">
        <div className="text-center py-12 text-text-dark text-lg">Validating upload link...</div>
      </div>
    );
  }

  // Show loading state while validating PIN
  if (validatingPin) {
    return (
      <div className="w-full p-8">
        <div className="text-center py-12 text-text-dark text-lg">Validating access...</div>
      </div>
    );
  }

  // Block access if PIN is not validated and user is not authenticated
  if (!pinValidated && !isAuthenticated && !uploadKey) {
    return (
      <>
        <div className="w-full p-8">
          <div className="text-center py-12 text-text-dark text-lg">Please enter PIN to upload photos</div>
        </div>
        <PinModal
          isOpen={showPinModal}
          onClose={() => navigate(-1)}
          expectedPin={uploadPin}
          onPinValidated={handlePinValidated}
          title="Enter Upload PIN"
          description="Enter 6-digit PIN to upload photos"
        />
      </>
    );
  }

  return (
    <div className="w-full p-8">
      <header className="flex items-center mb-8 pb-4 border-b border-gray-200 flex-col sm:flex-row gap-2 sm:gap-0 items-start sm:items-center">
        {(!uploadKey || (uploadKey && !albumMetadata?.isRestricted)) && (
          <button className="bg-none border-none text-accent text-base cursor-pointer py-2 pr-0 mr-4 transition-colors duration-200 flex items-center gap-2 hover:text-accent/80" onClick={() => navigate(`/album/${albumId}`)}>
            <span className="material-icons">arrow_back</span>
            Back to Album
          </button>
        )}
        <h1 className="m-0 text-text-dark text-3xl sm:text-2xl font-light font-serif">
          {uploadKey ? `Upload Photos to "${albumTitle}"` : `Add Photos to "${albumTitle}"`}
        </h1>
      </header>

      {uploadKey && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="m-0 text-blue-800 text-sm">
            You're using an anonymous upload link. Upload photos here and they'll be added to the album!
            {!albumMetadata?.isRestricted && ' You can view the album after uploading.'}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-8">
        <div 
          className="border-2 border-dashed border-gray-300 rounded-xl p-12 sm:p-8 text-center cursor-pointer transition-all duration-300 bg-gray-50 relative hover:border-accent hover:bg-accent/10"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <div className="flex flex-col items-center gap-4">
            <span className="material-icons text-6xl opacity-60 text-text-dark">add_photo_alternate</span>
            <h3 className="m-0 text-text-dark text-2xl sm:text-xl font-medium font-serif">Drag and drop photos/videos here</h3>
            <p className="m-0 text-text-dark/70 text-base">or click to select files</p>
            <input
              type="file"
              multiple
              accept="image/*,video/*,video/mp4,video/quicktime,video/x-msvideo,video/webm"
              onChange={handleFileSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>
        </div>

        {selectedFiles.length > 0 && (
          <div className="bg-primary border border-gray-200 rounded-xl p-8 shadow-md">
            <h3 className="m-0 mb-6 text-text-dark text-xl font-medium font-serif">{selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected</h3>

            <div className="space-y-2 mb-8">
              {selectedFiles.map((fileData) => {
                const isComplete = uploadProgress[fileData.id] === 100;
                const isUploading = uploading && uploadProgress[fileData.id] !== undefined && !isComplete;

                return (
                  <div key={fileData.id} className="relative bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors duration-200">
                    {/* Icon on the left */}
                    <div className="flex-shrink-0">
                      {isComplete ? (
                        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="material-icons text-white text-sm">check</span>
                        </div>
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="material-icons text-gray-400 text-2xl">
                            {fileData.isVideo ? 'videocam' : 'image'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* File info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-4 mb-1">
                        <span className="text-sm font-medium text-text-dark truncate" title={fileData.name}>
                          {fileData.name}
                        </span>
                        <span className="text-xs text-text-dark/70 flex-shrink-0">
                          {formatFileSize(fileData.size)}
                        </span>
                      </div>

                      {/* Progress bar */}
                      {isUploading && (
                        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-accent transition-all duration-300"
                            style={{ width: `${uploadProgress[fileData.id]}%` }}
                          />
                        </div>
                      )}

                      {isComplete && (
                        <span className="text-xs text-green-600 font-medium">Upload complete</span>
                      )}
                    </div>

                    {/* Remove button */}
                    {!uploading && !isComplete && (
                      <button
                        className="flex-shrink-0 w-8 h-8 border-none bg-transparent text-gray-400 rounded-full cursor-pointer flex items-center justify-center transition-colors duration-200 hover:bg-red-100 hover:text-red-600"
                        onClick={() => removeFile(fileData.id)}
                        title="Remove file"
                      >
                        <span className="material-icons text-base">close</span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center">
              <button 
                className="px-8 py-4 bg-accent text-text-light border-none rounded-lg text-lg font-medium cursor-pointer transition-colors duration-200 min-w-[200px] flex items-center justify-center gap-2 hover:bg-accent/80 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:text-text-light"
                onClick={uploadFiles}
                disabled={uploading || selectedFiles.length === 0}
              >
                <span className="material-icons">cloud_upload</span>
                {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoUploadPage;